import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { useTranslation } from 'react-i18next';

/**
 * PortfolioSection component for the homepage
 * Displays a selection of portfolio items
 * Designed with an elegant, minimalistic black and white approach
 * Supports multiple languages through i18n
 */
const PortfolioSection = () => {
  const { t } = useTranslation();
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.1 });
  
  // Sample portfolio items
  const portfolioItems = [
    {
      title: "Carried Away",
      description: "A physics-based puzzle game involving chairlifts and jumps.",
      image: "https://nativetranslationsfr.wordpress.com/wp-content/uploads/2017/09/logocarried.png",
      link: "/portfolio#carried-away"
    },
    {
      title: "Startup Company",
      description: "A business simulation game where players build and expand a startup.",
      image: "https://nativetranslationsfr.wordpress.com/wp-content/uploads/2017/08/logo.png",
      link: "/portfolio#startup-company"
    },
    {
      title: "SpaceJacked",
      description: "A fast-paced mix of tower defense and shooting.",
      image: "https://via.placeholder.com/600x400/000000/FFFFFF?text=SpaceJacked",
      link: "/portfolio#spacejacked"
    },
    {
      title: "Archaica: The Path of Light",
      description: "A mystical puzzle game featuring lasers and mirrors.",
      image: "https://nativetranslationsfr.wordpress.com/wp-content/uploads/2017/09/header1.jpg",
      link: "/portfolio#archaica"
    }
  ];

  // Animation variants
  const titleVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.8,
        ease: [0.25, 0.1, 0.25, 1]
      }
    }
  };
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }
    }
  };

  return (
    <section ref={sectionRef} className="py-32 bg-bg-alt scroll-section relative">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-1/3 h-px bg-primary/10"></div>
      
      <div className="container mx-auto px-6">
        <motion.div 
          className="max-w-3xl mx-auto mb-24 text-center"
          variants={titleVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          <div className="w-16 h-px bg-primary mx-auto mb-12"></div>
          <h2 className="font-heading text-3xl md:text-5xl font-normal mb-8 tracking-wide">
            {t('portfolio.title')}
          </h2>
          <p className="text-light text-lg md:text-xl font-light leading-relaxed max-w-2xl mx-auto">
            {t('portfolio.subtitle')}
          </p>
        </motion.div>

        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          {portfolioItems.map((item, index) => (
            <PortfolioItem 
              key={index}
              title={item.title}
              description={item.description}
              image={item.image}
              link={item.link}
              variants={itemVariants}
              index={index}
            />
          ))}
        </motion.div>
        
        <div className="text-center mt-20">
          <Link 
            to="/portfolio" 
            className="inline-block bg-primary text-white px-10 py-4 uppercase tracking-widest text-sm font-light transition-all duration-300 hover:bg-white hover:text-primary hover:border-primary border border-transparent"
          >
            {t('portfolio.viewFullPortfolio')}
          </Link>
        </div>
      </div>
    </section>
  );
};

/**
 * PortfolioItem component
 * Individual portfolio item card
 */
const PortfolioItem = ({ title, description, image, link, variants, index }) => {
  const { t } = useTranslation();
  // Calculate a slight delay based on index for hover animations
  const delay = 0.1 + (index * 0.05);
  
  return (
    <motion.div 
      className="group overflow-hidden border border-primary/10 hover:border-primary transition-all duration-500 reveal"
      variants={variants}
    >
      <Link to={link} className="block">
        <div className="relative overflow-hidden">
          <img 
            src={image} 
            alt={title} 
            className="w-full h-64 object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <div 
            className="absolute inset-0 bg-primary/80 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-center justify-center"
            style={{ transitionDelay: `${delay}s` }}
          >
            <span className="text-white font-light tracking-wider uppercase text-sm">{t('portfolio.viewDetails')}</span>
          </div>
        </div>
        <div className="p-6">
          <h3 className="text-xl font-heading mb-3 tracking-wide group-hover:text-primary transition-colors duration-300">{title}</h3>
          <p className="text-light font-light">{description}</p>
        </div>
      </Link>
    </motion.div>
  );
};

export default PortfolioSection;
